import type {
  DisplayMinimumKey,
  DisplayMinimumView,
  DisplayMinimumWrite,
} from "@screener/protocol";
import { isRecord } from "@/lib/guards";
import { DAEMON_REQUEST_TIMEOUT_MS } from "@/lib/request-timeout";

/**
 * HTTP-клиент личных минимумов §59 запросного канала §7.6: один путь
 * `/api/display-min` для трёх операций, как у избранного — запись не несёт
 * идентификатора, `DELETE` адресует её тем же ключом, каким `PUT` создаёт.
 * `400` — негодный запрос, `401` — нет сессии, прочее — хранилище.
 */

const DISPLAY_MIN_PATH = "/api/display-min";

function isDisplayMinimumView(value: unknown): value is DisplayMinimumView {
  return (
    isRecord(value) &&
    typeof value.exchange === "string" &&
    (value.market === "spot" || value.market === "perp") &&
    (typeof value.base === "string" || value.base === null) &&
    typeof value.min_usd === "string" &&
    (typeof value.daemon_floor_usd === "string" || value.daemon_floor_usd === null) &&
    typeof value.created_at_ms === "number"
  );
}

export type DisplayMinimumsListResult =
  | { status: "ok"; minimums: DisplayMinimumView[] }
  | { status: "unauthorized" }
  | { status: "unavailable" };

export async function fetchDisplayMinimums(): Promise<DisplayMinimumsListResult> {
  let response: Response;
  try {
    response = await fetch(DISPLAY_MIN_PATH, {
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (!response.ok) {
    console.error("список личных минимумов отклонён демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!Array.isArray(body) || !body.every(isDisplayMinimumView)) {
      return { status: "unavailable" };
    }
    return { status: "ok", minimums: body };
  } catch {
    return { status: "unavailable" };
  }
}

export type DisplayMinimumSaveResult =
  | { status: "ok"; minimum: DisplayMinimumView }
  | { status: "unauthorized" }
  | { status: "invalid" }
  | { status: "unavailable" };

/** Заводит минимум или заменяет число у существующего ключа. */
export async function saveDisplayMinimum(
  write: DisplayMinimumWrite,
): Promise<DisplayMinimumSaveResult> {
  let response: Response;
  try {
    response = await fetch(DISPLAY_MIN_PATH, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(write),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 400) {
    return { status: "invalid" };
  }
  if (!response.ok) {
    console.error("личный минимум не сохранён демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!isDisplayMinimumView(body)) {
      return { status: "unavailable" };
    }
    return { status: "ok", minimum: body };
  } catch {
    return { status: "unavailable" };
  }
}

export type DisplayMinimumRemoveResult =
  | { status: "ok" }
  | { status: "unauthorized" }
  | { status: "invalid" }
  | { status: "unavailable" };

export async function removeDisplayMinimum(
  key: DisplayMinimumKey,
): Promise<DisplayMinimumRemoveResult> {
  let response: Response;
  try {
    response = await fetch(DISPLAY_MIN_PATH, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(key),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 400) {
    return { status: "invalid" };
  }
  if (response.status === 204) {
    return { status: "ok" };
  }
  console.error("личный минимум не убран демоном", response.status);
  return { status: "unavailable" };
}
